import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, Sliders } from "lucide-react";
import { useT } from "@/i18n/TranslationContext";

const STORAGE_KEY = "ecominarg:consent-v2";

const defaultPrefs = {
  necessary: true,
  functional: true,
  analytics: false,
  marketing: false,
};

const applyConsent = (prefs) => {
  if (typeof window === "undefined") return;
  const gtag = window.gtag || function () { (window.dataLayer = window.dataLayer || []).push(arguments); };
  gtag("consent", "update", {
    ad_storage: prefs.marketing ? "granted" : "denied",
    ad_user_data: prefs.marketing ? "granted" : "denied",
    ad_personalization: prefs.marketing ? "granted" : "denied",
    analytics_storage: prefs.analytics ? "granted" : "denied",
    functionality_storage: prefs.functional ? "granted" : "denied",
    personalization_storage: prefs.functional ? "granted" : "denied",
    security_storage: "granted",
  });
  (window.dataLayer = window.dataLayer || []).push({
    event: "cookie_consent_update",
    consent: prefs,
  });
};

const CookieBanner = ({ langBasePath }) => {
  const { t } = useT();
  const [visible, setVisible] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        setPrefs({ ...defaultPrefs, ...saved });
        applyConsent({ ...defaultPrefs, ...saved });
        setVisible(false);
      } else {
        setVisible(true);
      }
    } catch (e) {
      setVisible(true);
    }
  }, []);

  const save = (nextPrefs) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...nextPrefs, savedAt: Date.now() }));
    } catch (e) {}
    applyConsent(nextPrefs);
    setPrefs(nextPrefs);
    setVisible(false);
    setShowManage(false);
  };

  if (!visible) return null;

  const categories = [
    { key: "necessary", locked: true, ...t("cookie.categories.necessary") },
    { key: "functional", locked: false, ...t("cookie.categories.functional") },
    { key: "analytics", locked: false, ...t("cookie.categories.analytics") },
    { key: "marketing", locked: false, ...t("cookie.categories.marketing") },
  ];

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookies"
      className="fixed inset-x-0 bottom-0 z-50 px-3 sm:px-5 pb-4 pointer-events-none"
    >
      <div className="pointer-events-auto max-w-5xl mx-auto bg-white shadow-2xl border border-[#0b2545]/15 rounded-2xl overflow-hidden">
        {!showManage ? (
          <div className="p-5 sm:p-6 flex flex-col lg:flex-row gap-4 lg:items-center">
            <div className="flex items-start gap-3 flex-1">
              <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#0b2545]/8 text-[#0b2545]">
                <Cookie className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <h3 className="font-bold text-[#0b2545] text-base">{t("cookie.title")}</h3>
                <p className="text-sm text-[#0b2545]/75 mt-1 leading-relaxed">
                  {t("cookie.desc")}{" "}
                  {langBasePath && (
                    <Link
                      to={`${langBasePath}/cookie-policy`}
                      className="underline underline-offset-2 text-[#0b2545] hover:text-[#c46a3d]"
                    >
                      {t("footer.cookies")}
                    </Link>
                  )}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => save({ necessary: true, functional: true, analytics: false, marketing: false })}
                className="px-4 py-2.5 rounded-full text-sm font-semibold text-[#0b2545] border border-[#0b2545]/25 hover:bg-[#0b2545]/5"
                data-testid="cookie-reject"
              >
                {t("cookie.reject")}
              </button>
              <button
                type="button"
                onClick={() => setShowManage(true)}
                className="px-4 py-2.5 rounded-full text-sm font-semibold text-[#0b2545] border border-[#0b2545]/25 hover:bg-[#0b2545]/5 inline-flex items-center justify-center gap-1.5"
                data-testid="cookie-manage"
              >
                <Sliders className="w-4 h-4" /> {t("cookie.manage")}
              </button>
              <button
                type="button"
                onClick={() => save({ necessary: true, functional: true, analytics: true, marketing: true })}
                className="px-4 py-2.5 rounded-full text-sm font-semibold text-white bg-[#0b2545] hover:bg-[#13315c] inline-flex items-center justify-center gap-1.5"
                data-testid="cookie-accept"
              >
                <ShieldCheck className="w-4 h-4" /> {t("cookie.accept")}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <h3 className="font-bold text-[#0b2545] text-lg">{t("cookie.manage")}</h3>
            <p className="text-sm text-[#0b2545]/70 mt-1">{t("cookie.desc")}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {categories.map((c) => (
                <label
                  key={c.key}
                  className={`flex items-start gap-3 rounded-xl border p-3 ${
                    c.locked ? "border-[#0b2545]/10 bg-[#0b2545]/[0.03]" : "border-[#0b2545]/15"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={c.locked ? true : prefs[c.key]}
                    disabled={c.locked}
                    onChange={(e) => setPrefs((p) => ({ ...p, [c.key]: e.target.checked }))}
                    className="mt-1 accent-[#0b2545]"
                    data-testid={`cookie-cat-${c.key}`}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#0b2545] text-sm">{c.title}</span>
                      {c.locked && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#0b2545]/10 text-[#0b2545]">
                          {t("cookie.always_on")}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#0b2545]/70 mt-1 leading-relaxed">{c.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowManage(false)}
                className="px-4 py-2.5 rounded-full text-sm font-semibold text-[#0b2545] border border-[#0b2545]/25 hover:bg-[#0b2545]/5"
              >
                {t("cookie.back")}
              </button>
              <button
                type="button"
                onClick={() => save(prefs)}
                className="px-4 py-2.5 rounded-full text-sm font-semibold text-white bg-[#0b2545] hover:bg-[#13315c]"
                data-testid="cookie-save"
              >
                {t("cookie.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
